import { useState, useEffect } from 'react'
import { useTelegram } from '../hooks/useTelegram'
import ImageUploader from './ImageUploader'
import { createPhotosession, getPhotosessionStyles } from '../services/api'
import './PhotoSession.css'

interface Style {
  name: string
  description: string
}

function PhotoSession() {
  const { showAlert, user } = useTelegram()
  const [referenceFile, setReferenceFile] = useState<File | null>(null)
  const [referencePreview, setReferencePreview] = useState<string | null>(null)
  const [selectedStyle, setSelectedStyle] = useState<string>('studio')
  const [styles, setStyles] = useState<Record<string, Style>>({})
  const [generating, setGenerating] = useState(false)
  const [resultImage, setResultImage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Загружаем доступные стили
    getPhotosessionStyles()
      .then((data) => {
        setStyles(data.styles || {})
      })
      .catch((err) => {
        console.error('Error loading styles:', err)
        // Используем стили по умолчанию
        setStyles({
          studio: { name: 'studio', description: 'Студийная фотосессия' },
          outdoor: { name: 'outdoor', description: 'Уличная фотосессия' },
          fashion: { name: 'fashion', description: 'Модная фотосессия' },
          casual: { name: 'casual', description: 'Повседневная фотосессия' },
          artistic: { name: 'artistic', description: 'Художественная фотосессия' },
        })
      })
  }, [])

  const handleReferenceUpload = (_filename: string, preview: string, file: File) => {
    setReferenceFile(file)
    setReferencePreview(preview)
    setError(null)
    setResultImage(null)
  }

  const handleGenerate = async () => {
    if (!referenceFile) {
      showAlert('Пожалуйста, загрузите референсное изображение')
      return
    }

    setGenerating(true)
    setError(null)
    setResultImage(null)

    try {
      // Получаем customer_id из Telegram user ID
      let customerId = user?.id?.toString()
      if (!customerId) {
        // Для разработки вне Telegram используем тестовый ID
        customerId = '123456789'
        console.warn('Telegram user ID не найден, используем тестовый ID:', customerId)
      }
      const blob = await createPhotosession(referenceFile, selectedStyle, customerId)
      const imageUrl = URL.createObjectURL(blob)
      setResultImage(imageUrl)
      showAlert('Фотосессия успешно создана!')
    } catch (err: any) {
      const errorMsg = err.message || 'Ошибка при генерации фотосессии'
      setError(errorMsg)
      showAlert(errorMsg)
      console.error('Photosession generation error:', err)
    } finally {
      setGenerating(false)
    }
  }

  const handleDownload = () => {
    if (!resultImage) return

    const link = document.createElement('a')
    link.href = resultImage
    link.download = `photosession-${selectedStyle}-${Date.now()}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="photo-session">
      <h2>Фотосессия по референсу</h2>
      <p className="description">
        Загрузите референсное фото, и AI создаст профессиональную фотосессию в выбранном стиле
      </p>

      <div className="upload-section">
        <h3>1. Загрузите референсное изображение</h3>
        <ImageUploader
          onUploadSuccess={handleReferenceUpload}
          onUploadError={(err) => {
            setError(err)
            showAlert(err)
          }}
          maxSize={10}
          accept="image/*"
          label="Выбрать фото"
        />
        {referencePreview && (
          <div className="preview-container">
            <img src={referencePreview} alt="Reference" className="preview-image" />
          </div>
        )}
      </div>

      <div className="style-section">
        <h3>2. Выберите стиль фотосессии</h3>
        <div className="styles-grid">
          {Object.entries(styles).map(([key, style]) => (
            <button
              key={key}
              className={`style-button ${selectedStyle === key ? 'active' : ''}`}
              onClick={() => setSelectedStyle(key)}
            >
              <div className="style-name">{style.name}</div>
              <div className="style-description">{style.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="generate-section">
        <button
          className="generate-button"
          onClick={handleGenerate}
          disabled={!referenceFile || generating}
        >
          {generating ? 'Генерация...' : 'Создать фотосессию'}
        </button>
        {generating && (
          <div className="generating-info">
            <p>Генерация может занять 1-3 минуты...</p>
            <div className="spinner"></div>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
          <p className="error-hint">
            Убедитесь, что YESAI_API_TOKEN установлен в backend/.env
          </p>
        </div>
      )}

      {resultImage && (
        <div className="result-section">
          <h3>Результат</h3>
          <div className="result-container">
            <img src={resultImage} alt="Generated photosession" className="result-image" />
          </div>
          <button className="download-button" onClick={handleDownload}>
            📥 Скачать результат
          </button>
        </div>
      )}
    </div>
  )
}

export default PhotoSession

